import { Modal } from "react-bootstrap";
import React, { useEffect, useState, useRef, useCallback } from "react";
import useCheckIsMobileScreen from "../../../../hooks/checkMobileScreen";
import styles from "./CustomModal.module.css";

const CustomModal = ({
  children,
  show,
  mobileCloseButton = true,
  onHide,
  size,
  mobileFixedCloseButton = false,
  ...props
}) => {
  const [zIndex, setZIndex] = useState(1050);
  const { isMobile } = useCheckIsMobileScreen();
  const modalRef = useRef(null);
  const backdropRef = useRef(null);
  const observerRef = useRef(null);

  const updateModalStack = useCallback(() => {
    const openModals = Array.from(document.querySelectorAll(".modal.show"));
    if (!openModals.length) return;

    if (!modalRef.current) return;
    const modalIndex = openModals.indexOf(modalRef.current);
    if (modalIndex === -1) return;

    const newZIndex = 1050 + modalIndex * 10;
    setZIndex(newZIndex);

    if (backdropRef.current) {
      backdropRef.current.style.zIndex = `${newZIndex - 5}`;
    }
  }, []);

  useEffect(() => {
    observerRef.current = new MutationObserver((mutations) => {
      const relevantChanges = mutations.some(
        (mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.classList &&
              (node.classList.contains("modal") ||
                node.classList.contains("modal-backdrop"))
          ) ||
          Array.from(mutation.removedNodes).some(
            (node) =>
              node.classList &&
              (node.classList.contains("modal") ||
                node.classList.contains("modal-backdrop"))
          )
      );

      if (relevantChanges) {
        updateModalStack();
      }
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [updateModalStack]);

  useEffect(() => {
    if (show && modalRef.current) {
      const findBackdrop = () => {
        const backdrops = Array.from(
          document.querySelectorAll(".modal-backdrop.show")
        );
        if (backdrops.length > 0) {
          backdropRef.current = backdrops[backdrops.length - 1];
          updateModalStack();
        }
      };

      // We need a slight delay to ensure the backdrop is in the DOM
      const timer = requestAnimationFrame(findBackdrop);
      return () => cancelAnimationFrame(timer);
    }
  }, [show, updateModalStack]);

  const handleEntered = (e) => {
    if (props.onEntered) {
      props.onEntered(e);
    }
    updateModalStack();
  };

  // Check if there's a Modal.Header among the children
  const hasModalHeader = React.Children.toArray(children).some((child) => {
    if (child && child.type === Modal.Header) return true;

    // Check for nested Modal.Header
    if (child && child.props && child.props.children) {
      return React.Children.toArray(child.props.children).some(
        (grandChild) => grandChild && grandChild.type === Modal.Header
      );
    }

    return false;
  });

  const processedChildren = React.Children.map(children, (child) => {
    if (!child) return child;

    if (child.type === Modal.Header) {
      const shouldShowCloseButton =
        child.props.closeButton === true || (isMobile && mobileCloseButton);

      const newStyle = {
        ...(child.props.style || {}),
        borderBottom: "none",
      };

      return React.cloneElement(child, {
        closeButton: shouldShowCloseButton,
        style: newStyle,
      });
    }

    if (child.props && child.props.children) {
      return React.cloneElement(child, {
        children: React.Children.map(child.props.children, (grandChild) => {
          if (grandChild && grandChild.type === Modal.Header) {
            const shouldShowCloseButton =
              grandChild.props.closeButton === true ||
              (isMobile && mobileCloseButton);

            const newStyle = {
              ...(grandChild.props.style || {}),
              borderBottom: "none",
            };

            return React.cloneElement(grandChild, {
              closeButton: shouldShowCloseButton,
              style: newStyle,
            });
          }
          return grandChild;
        }),
      });
    }

    return child;
  });

  const showFloatingCloseButton =
    isMobile && mobileCloseButton && !hasModalHeader;

  const modalProps = { ...props };
  let dialogClassName = props.dialogClassName || "";

  if (size === "xxl") {
    // For xxl size, don't pass size to Bootstrap Modal, use custom class instead
    dialogClassName = `modal-xxl ${dialogClassName}`.trim();
  } else {
    modalProps.size = size;
  }

  if (dialogClassName) {
    modalProps.dialogClassName = dialogClassName;
  }

  return (
    <Modal
      ref={modalRef}
      show={show}
      onHide={onHide}
      {...modalProps}
      style={{ zIndex }}
      onEntered={handleEntered}
    >
      {processedChildren}
      {showFloatingCloseButton && (
        <div
          className={`${styles.floatingCloseButtonContainer} ${
            isMobile && mobileFixedCloseButton
              ? styles.mobileFixedCloseButton
              : ""
          }`}
        >
          <button onClick={onHide} className={styles.floatingCloseButton}>
            <svg
              className={styles.closeIcon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M18 6L6 18M6 6L18 18" className={styles.closeIconPath} />
            </svg>
          </button>
        </div>
      )}
    </Modal>
  );
};

export default CustomModal;
